import type { AnyRouter } from '@tanstack/react-router';
import { updateState } from './update-state';

export interface BetweenScreensOptions {
  /** Why the tab must not reload itself now, or null (reload-hold.ts). */
  held: () => string | null;
  /** A full page load of `href` (the app's own path, search and hash). */
  load: (href: string) => void;
  isUpdated?: () => boolean;
}

/**
 * Once a newer Paige is deployed, the next move to another screen is a full page load of that
 * screen instead of a client-side one: the person asked for a new screen anyway, so nothing they
 * were looking at disappears, and the screen they get is the new Paige's. A move within the screen
 * (search or hash only, as the workbench's queue does) stays client-side, and nothing reloads while
 * the page is held — the banner says Paige was updated and the person reloads when they are ready.
 * Returns the way to stop.
 */
export function reloadBetweenScreens(
  router: AnyRouter,
  options: BetweenScreensOptions,
): () => void {
  const isUpdated = options.isUpdated ?? (() => updateState().updated);
  return router.subscribe('onBeforeNavigate', (event) => {
    if (!event.fromLocation || !event.pathChanged || !isUpdated()) return;
    if (options.held() !== null) return;
    options.load(event.toLocation.href);
  });
}
